import { IMergeCandidate } from 'src/interfaces/MergeCandidate';

/** Normalize email for duplicate matching (trim + lowercase). */
export function normalizeMergeEmail(email: string | null | undefined): string {
  return (email ?? '').trim().toLowerCase();
}

/** Normalize phone to digits only for duplicate matching. */
export function normalizeMergePhone(phone: string | null | undefined): string {
  return (phone ?? '').replace(/\D/g, '');
}

export function emailsMatch(
  a: string | null | undefined,
  b: string | null | undefined,
): boolean {
  const na = normalizeMergeEmail(a);
  const nb = normalizeMergeEmail(b);
  return Boolean(na) && na === nb;
}

export function phonesMatch(
  a: string | null | undefined,
  b: string | null | undefined,
): boolean {
  const na = normalizeMergePhone(a);
  const nb = normalizeMergePhone(b);
  return Boolean(na) && na === nb;
}

/** True when the candidate shares email or phone with any selected candidate. */
export function isMergeContactMatch(
  candidate: Pick<IMergeCandidate, 'id' | 'email' | 'phoneNumber'>,
  selected: Array<Pick<IMergeCandidate, 'id' | 'email' | 'phoneNumber'>>,
): boolean {
  if (!selected.length) return false;
  if (selected.some(s => s.id === candidate.id)) return false;

  return selected.some(
    s =>
      emailsMatch(candidate.email, s.email) ||
      phonesMatch(candidate.phoneNumber, s.phoneNumber),
  );
}

/** Email or normalized phone appears on more than one candidate. */
export function buildLikelyDuplicateIdSet(candidates: IMergeCandidate[]): Set<string> {
  const emailCounts = new Map<string, string[]>();
  const phoneCounts = new Map<string, string[]>();

  for (const c of candidates) {
    const email = normalizeMergeEmail(c.email);
    if (email) {
      const list = emailCounts.get(email) ?? [];
      list.push(c.id);
      emailCounts.set(email, list);
    }
    const phone = normalizeMergePhone(c.phoneNumber);
    if (phone) {
      const list = phoneCounts.get(phone) ?? [];
      list.push(c.id);
      phoneCounts.set(phone, list);
    }
  }

  const duplicateIds = new Set<string>();
  for (const ids of emailCounts.values()) {
    if (ids.length > 1) ids.forEach(id => duplicateIds.add(id));
  }
  for (const ids of phoneCounts.values()) {
    if (ids.length > 1) ids.forEach(id => duplicateIds.add(id));
  }
  return duplicateIds;
}

export function filterMergeCandidates(
  candidates: IMergeCandidate[],
  {
    query,
    likelyDuplicatesOnly,
  }: { query: string; likelyDuplicatesOnly: boolean },
): IMergeCandidate[] {
  const duplicateIds = likelyDuplicatesOnly
    ? buildLikelyDuplicateIdSet(candidates)
    : null;

  const q = query.trim().toLowerCase();
  const phoneQ = query.replace(/\D/g, '');

  return candidates.filter(c => {
    if (duplicateIds && !duplicateIds.has(c.id)) return false;
    if (!q && !phoneQ) return true;

    const name = `${c.lastName} ${c.firstName}`.toLowerCase();
    const email = normalizeMergeEmail(c.email);
    const phone = normalizeMergePhone(c.phoneNumber);

    return (
      name.includes(q) ||
      email.includes(q) ||
      (phoneQ.length > 0 && phone.includes(phoneQ)) ||
      (q.length > 0 && phone.includes(q))
    );
  });
}

/** Newest by createdAt, then updatedAt — mirrors server survivor selection for confirm UI. */
export function pickSurvivorPreview(candidates: IMergeCandidate[]): IMergeCandidate | null {
  if (!candidates.length) return null;
  const sorted = [...candidates].sort((a, b) => {
    const createdDiff =
      new Date(b.createdAt ?? 0).getTime() - new Date(a.createdAt ?? 0).getTime();
    if (createdDiff !== 0) return createdDiff;
    return (
      new Date(b.updatedAt ?? 0).getTime() - new Date(a.updatedAt ?? 0).getTime()
    );
  });
  return sorted[0] ?? null;
}

export function collectEditionYears(candidates: IMergeCandidate[]): number[] {
  const years = new Set<number>();
  for (const c of candidates) {
    for (const ed of c.editions ?? []) {
      if (ed.year != null) years.add(ed.year);
    }
  }
  return [...years].sort((a, b) => b - a);
}
